import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { ArrowLeft, Inbox } from 'lucide-react';
import { useCatalogsStore } from '@/stores/catalogs';
import type { ProposalSource } from '@/services/api';
import {
  ProposedCanonicalsQueueList,
  ProposedReviewPanel,
} from '@/components/knowledge/catalogs/ProposedCanonicalsQueue';

const SOURCE_OPTIONS: Array<{ value: ProposalSource | ''; label: string; description?: string }> = [
  { value: '', label: 'All sources' },
  { value: 'uos', label: 'UOS', description: 'Universal Ontological Schema extensions' },
  { value: 'concept_type', label: 'ConceptType', description: 'Pending ConceptType candidates' },
  { value: 'ad_taxonomy', label: 'Ad Taxonomy', description: 'Dynamically-discovered creative taxonomy nodes' },
  { value: 'concept_type_axis', label: 'Layer 1 Axes', description: 'Newly-discovered axis slugs from lens extraction (Lens Unification v2)' },
];

const AD_AXIS_SUB_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All ad axes' },
  { value: 'creative_template', label: 'Creative Templates' },
  { value: 'layout_archetype', label: 'Layout Archetypes' },
  { value: 'visual_style', label: 'Visual Styles' },
  { value: 'hook', label: 'Hooks' },
  { value: 'angle', label: 'Angles' },
  { value: 'cta', label: 'CTAs' },
  { value: 'proof', label: 'Proof Elements' },
];

export function ProposedCanonicalsPage() {
  const {
    queueFilters,
    queueCountsBySource,
    queueAdAxisCounts,
    setQueueFilters,
    fetchQueue,
  } = useCatalogsStore();

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const total =
    (queueCountsBySource.uos ?? 0) +
    (queueCountsBySource.concept_type ?? 0) +
    (queueCountsBySource.ad_taxonomy ?? 0) +
    (queueCountsBySource.concept_type_axis ?? 0);

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-3">
          <Link
            to="/knowledge/catalogs"
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft size={14} />
            Catalogs
          </Link>
          <div className="flex items-center gap-2">
            <Inbox size={18} className="text-brand-teal" />
            <div>
              <h1 className="text-xl font-semibold text-navy-900">Proposed Canonicals</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                Unified inbox for UOS extensions, ConceptType candidates, Ad
                Taxonomy elements, and Layer 1 axis proposals
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {SOURCE_OPTIONS.map((opt) => {
            const active = queueFilters.source_type === opt.value;
            const count =
              opt.value === ''
                ? total
                : queueCountsBySource[opt.value as ProposalSource];
            return (
              <button
                key={opt.value || 'all'}
                onClick={() =>
                  setQueueFilters({ source_type: (opt.value || '') as ProposalSource | '' })
                }
                className={clsx(
                  'px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors',
                  active
                    ? 'bg-brand-teal/10 border-brand-teal/30 text-brand-teal-dark'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300',
                )}
              >
                {opt.label}
                <span className="ml-1 text-[10px] text-slate-400">({count})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Phase 5: sub-axis chip rail surfaces only when ad_taxonomy is the
          active source filter. Powered by ad_axis_counts in the API response. */}
      {queueFilters.source_type === 'ad_taxonomy' && (
        <div className="flex items-center gap-1 px-6 py-2 border-b border-slate-200 bg-white flex-wrap">
          <span className="text-[11px] uppercase tracking-wide text-slate-500 mr-2">
            Axis:
          </span>
          {AD_AXIS_SUB_OPTIONS.map((opt) => {
            const active = (queueFilters.ad_axis_slug || '') === opt.value;
            const count =
              opt.value === ''
                ? Object.values(queueAdAxisCounts).reduce((a, b) => a + b, 0)
                : queueAdAxisCounts[opt.value] ?? 0;
            return (
              <button
                key={opt.value || 'all_axes'}
                onClick={() => setQueueFilters({ ad_axis_slug: opt.value })}
                className={clsx(
                  'px-2.5 py-1 text-xs font-medium rounded-full border transition-colors',
                  active
                    ? 'bg-violet-100 border-violet-300 text-violet-800'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300',
                )}
              >
                {opt.label}
                <span className="ml-1 text-[10px] text-slate-400 tabular-nums">
                  ({count})
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        <div className="w-[50%] p-4 overflow-auto">
          <ProposedCanonicalsQueueList />
        </div>
        <div className="flex-1 min-w-0 p-4 overflow-auto border-l border-slate-200">
          <ProposedReviewPanel />
        </div>
      </div>
    </div>
  );
}
